import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Select, { components } from "react-select";

import { pageLoading } from "../../redux/selectors/pagesSelectors";
import { CUSTOM_BILLS_ID, FEATURED_PRODUCTS_PAIR } from "../../constants/constants";

import { setCartSelectedBills as setCartSelectedBillsAction } from "../../redux/actions/cartAction";
import {
    fetchPaymentMenu,
    saveProductId,
    setCustomBills as setCustomBillsAction
} from "../../redux/actions/pagesActions";
import Skeleton from "react-loading-skeleton";
import { getCurrentPaymentUiMode } from "../../lib/paymentUiMode";

export default function ProductMenu() {
    const dispatch = useDispatch();
    const isPageLoading = useSelector(pageLoading);

    const [optionItem, setOptionItem] = useState(null);
    const [filteredOptions, setFilteredOptions] = useState([]);

    const [searchParams] = useSearchParams();
    const locationObj = useLocation();
    const navigate = useNavigate();

    const setProductId = useCallback((data) => dispatch(saveProductId(data)), [dispatch]);
    const setCartSelectedBills = useCallback((data) => dispatch(setCartSelectedBillsAction(data)), [dispatch]);
    const setCustomBills = useCallback((data) => dispatch(setCustomBillsAction(data)), [dispatch]);
    const setPaymentMenu = useCallback(() => dispatch(fetchPaymentMenu()), [dispatch]);

    const DropdownIndicator = (props) => (
        <components.DropdownIndicator {...props}> </components.DropdownIndicator>
    );

    const baseOptions = useMemo(() => {
        const options = [];
        for (const i in FEATURED_PRODUCTS_PAIR) {
            if (FEATURED_PRODUCTS_PAIR[i].allowedAsSeparate) {
                options.push({
                    value: isNaN(Number(i)) ? i : +i,
                    label: FEATURED_PRODUCTS_PAIR[i].name,
                    description: FEATURED_PRODUCTS_PAIR[i].description,
                    isDisabled: !FEATURED_PRODUCTS_PAIR[i].active,
                });
            }
        }
        return options;
    }, []);

    useEffect(() => {
        if (baseOptions.length === 0) return;

        setPaymentMenu().then((data) => {
            const bills = data?.custom_bills;
            let updatedOptions = baseOptions;

            if (!bills?.bills || bills.bills.length === 0) {
                updatedOptions = baseOptions.filter((option) => option.value !== CUSTOM_BILLS_ID);
            }

            setCustomBills(bills);
            setFilteredOptions(updatedOptions);
        });
    }, [baseOptions, setCustomBills, setPaymentMenu]);

    useEffect(() => {
        const productParamId = searchParams.get("product_id");

        if (locationObj.pathname.startsWith("/bills")) {
            setOptionItem(filteredOptions.find((opt) => opt.value === CUSTOM_BILLS_ID) ?? {});
            return;
        }

        if (productParamId) {
            setOptionItem(filteredOptions.find((opt) => opt.value === +productParamId) ?? {});
        }

        setCartSelectedBills({});
    }, [filteredOptions, locationObj.pathname, searchParams, setCartSelectedBills]);
    const Option = (props) => {
        return (
            <components.Option {...props}>
                <div>
                    <p className={'title'}>{props.data.label}</p>
                    <p className={'description'}>{props.data.description}</p>
                </div>
            </components.Option>
        );
    };

    return (
        <>
            <div className={'relative filter__wrapper--product'}>
                {isPageLoading ? (
                    <Skeleton
                        width={200}
                        height={32}
                        inline={true}
                        containerClassName={'mr-13'}
                    />
                ) : (
                    <>
                        <label
                            htmlFor="categoryList"
                            className={'filter__label filter__label--product text-5'}
                        >
                            Product
                        </label>
                        <Select
                            id={'productList'}
                            options={filteredOptions}
                            className={
                                'filter__selector ml-2 mr-16 tablet-mr-2 product-selector filter__selector--product'
                            }
                            classNamePrefix="filter"
                            isSearchable={false}
                            components={{
                                DropdownIndicator,
                                Option,
                            }}
                            onChange={(e) => {
                                const nextSearchParams = new URLSearchParams(locationObj.search);
                                const paymentUiMode = getCurrentPaymentUiMode();

                                setOptionItem(e)
                                if (e.value === "custom_bills") {
                                    const billsSearchParams = new URLSearchParams();

                                    if (paymentUiMode) {
                                        billsSearchParams.set('ui_mode', paymentUiMode);
                                    }

                                    navigate(`/bills${billsSearchParams.toString() ? `?${billsSearchParams.toString()}` : ''}`, { replace: true });
                                    return
                                }

                                setProductId(isNaN(Number(e.value)) ? e.value : Number(e.value));
                                nextSearchParams.set('product_id', e.value);
                                navigate(`/${nextSearchParams.toString() ? `?${nextSearchParams.toString()}` : ''}`, { replace: false })
                            }}
                            isOptionDisabled={(option) => option?.isDisabled}
                            defaultValue={optionItem}
                            value={optionItem}
                        />
                    </>
                )}
            </div>
        </>
    );
}
